/**
 * The dataset form — create or edit one tracker dataset.
 *
 * A dataset is a source, two queries, a workflow mapping and the adapter
 * config that says which tracker fields Shipcast reads. Every one of those
 * has a control of its own here; the JSON escape hatch appears only for keys
 * this build has no control for.
 */

import { useEffect, useId, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Controller, useForm, useWatch, type Control, type UseFormRegister } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import { Button, Input } from '@lean-agility/design-system/react';

import { Dialog, DialogActions } from '@/components/dialog';
import { SettingRow, SettingSubField, SettingsSection, SettingsTable } from './SettingRow';
import { StepNav } from './StepNav';
import { Checkbox } from '@/components/atoms';
import { SearchableSelect, type SearchableSelectOption } from '@/components/molecules';
import {
  useBoardScaffold,
  useCreateDataset,
  useLatestIngestion,
  useSourceBoards,
  useSourceFields,
  useSourceLinkTypes,
  useUpdateDataset,
} from '../hooks';
import {
  DEFAULT_ADAPTER_CONFIG,
  DEFAULT_DEPARTURE_COLUMN,
  DEFAULT_WORKFLOW,
  type BoardScaffoldResponse,
  type DatasetPreviewRequest,
  type DatasetResponse,
  type SourceResponse,
  type StoredAdapterConfig,
  type TrackerField,
} from '../types';
import { DatasetPreviewPanel } from './DatasetPreviewPanel';
import { QueryPreviewPanel } from './QueryPreviewPanel';
import { WorkflowEditor } from './WorkflowEditor';
import { parseWorkflowColumns } from '../workflowJson';

const jsonString = z
  .string()
  .min(1)
  .refine((s) => {
    try {
      JSON.parse(s);
      return true;
    } catch {
      return false;
    }
  }, 'Must be valid JSON');

const schema = z.object({
  name: z.string().min(1, 'Name is required'),
  tracker_source_id: z.string().uuid('Pick a source'),
  deliverables_query: z.string().min(1, 'Deliverables query is required'),
  work_items_query: z.string().min(1, 'Work-items query is required'),
  workflow_json: jsonString,
  /** The children knob (child → deliverable). A field selector: `parent`, or a custom field id. */
  parent_field: z.string().min(1, 'Parent field is required'),
  /** The deliverable → feature link. Empty = not configured (Shipcast owns the grouping). */
  feature_link_field: z.string(),
  /** Deliverable due date. Empty = unmapped, so Shipcast owns it — see {@link splitAdapterConfig}. */
  due_date_field: z.string(),
  /** Low estimate of unknown work. Empty = unmapped. */
  unknown_min_field: z.string(),
  /** High estimate of unknown work. Empty = unmapped. */
  unknown_max_field: z.string(),
  /**
   * Tracker link-type name that marks a dependency. Required, because an
   * empty name matches no link and would import no dependencies at all —
   * silently. Turning dependencies off is a query's job, not a blank field's.
   */
  dependency_link_type: z.string().min(1, 'Dependency link type is required'),
  /** The workflow column that counts as "shipped". Purely local — a name from the editor above. */
  departure_column: z.string().min(1, 'Departure column is required'),
  /** Every other `adapter_config` key, as JSON. Empty for anything this build writes. */
  adapter_config_json: jsonString,
});

type FormData = z.infer<typeof schema>;

/** The form fields that own an `adapter_config` key — everything the JSON no longer holds. */
type PickedAdapterFields = Pick<
  FormData,
  | 'parent_field'
  | 'feature_link_field'
  | 'due_date_field'
  | 'unknown_min_field'
  | 'unknown_max_field'
  | 'dependency_link_type'
  | 'departure_column'
>;

/**
 * Split a stored adapter config into the picker-owned keys and the rest.
 *
 * Every key this build knows has a control of its own; the JSON textarea
 * shows only what is left, which for a config written by this build is
 * nothing. Keeping a key out of the textarea is what makes its control the
 * single place it is edited — a textarea that also carried it would need
 * two-way sync, and the loser of that sync is whichever the user did not
 * look at last.
 *
 * The textarea is not dead weight even when empty: `StoredAdapterConfig` is
 * a deliberately tolerant reader (see `crates/api/src/ingestion/config.rs`),
 * so a row written by a *later* shipcast can carry a key this build has no
 * control for. That key lands here and round-trips instead of being dropped.
 *
 * **`due_date_field` is tri-state and the empty string means `null`, not
 * absent.** Absent is the tracker-owned default (`"due_date"`); `null` is
 * Shipcast-owned, which makes imports preserve local edits. Reading an
 * absent key as the default is exactly what the server does; writing empty
 * back as absent instead of `null` would silently re-map the field and start
 * overwriting hand-edited due dates.
 */
function splitAdapterConfig(cfg: StoredAdapterConfig): { picked: PickedAdapterFields; restJson: string } {
  const {
    parent_field,
    feature_link,
    due_date_field,
    unknown_min_field,
    unknown_max_field,
    dependency_link_type,
    departure_column,
    ...rest
  } = cfg;
  return {
    picked: {
      parent_field: parent_field ?? DEFAULT_ADAPTER_CONFIG.parent_field,
      feature_link_field: feature_link?.kind === 'parent_field' ? feature_link.field : '',
      // `?? ''` collapses only `null`/`undefined`; `undefined` (key absent)
      // takes the server's own default rather than reading as unmapped.
      due_date_field: due_date_field === undefined ? DEFAULT_ADAPTER_CONFIG.due_date_field : (due_date_field ?? ''),
      unknown_min_field: unknown_min_field ?? '',
      unknown_max_field: unknown_max_field ?? '',
      dependency_link_type: dependency_link_type ?? DEFAULT_ADAPTER_CONFIG.dependency_link_type,
      departure_column: departure_column ?? DEFAULT_ADAPTER_CONFIG.departure_column,
    },
    restJson: JSON.stringify(rest, null, 2),
  };
}

/** Inverse of {@link splitAdapterConfig}: the controls win over any copy pasted into the JSON. */
function mergeAdapterConfig(restJson: string, picked: PickedAdapterFields): StoredAdapterConfig {
  const rest: StoredAdapterConfig = JSON.parse(restJson);
  delete rest.parent_field;
  delete rest.feature_link;
  delete rest.due_date_field;
  delete rest.unknown_min_field;
  delete rest.unknown_max_field;
  delete rest.dependency_link_type;
  delete rest.departure_column;
  return {
    ...rest,
    parent_field: picked.parent_field,
    dependency_link_type: picked.dependency_link_type,
    departure_column: picked.departure_column,
    // Explicit `null` is the wire form of "not configured" — for the feature
    // link (docs/tracker-integration.md) and for each optional field below.
    // `null`, never `undefined`: an absent `due_date_field` means *mapped*.
    feature_link: picked.feature_link_field ? { kind: 'parent_field', field: picked.feature_link_field } : null,
    due_date_field: picked.due_date_field || null,
    unknown_min_field: picked.unknown_min_field || null,
    unknown_max_field: picked.unknown_max_field || null,
  };
}

/** The picker's option for a discovered field — value = the id the config stores. */
/**
 * The unsaved dataset exactly as the API reads it — everything but the name.
 *
 * One builder for two callers: Save spreads it into the create/update body,
 * and the pre-save preview posts it as-is. That is what makes the preview a
 * dry run of *this* Save rather than of a config that merely resembles it —
 * a second builder here would be a second place for the two to disagree.
 *
 * Takes the form's live values, which react-hook-form types as partial;
 * every field has a default, so a missing one is read as empty. Throws where
 * `JSON.parse` does — on the workflow or advanced JSON the form's own schema
 * is already reporting as invalid.
 */
function datasetBody(data: Partial<FormData>): DatasetPreviewRequest {
  return {
    tracker_source_id: data.tracker_source_id ?? '',
    deliverables_query: data.deliverables_query ?? '',
    work_items_query: data.work_items_query ?? '',
    workflow: JSON.parse(data.workflow_json ?? ''),
    adapter_config: mergeAdapterConfig(data.adapter_config_json ?? '', {
      parent_field: data.parent_field ?? '',
      feature_link_field: data.feature_link_field ?? '',
      due_date_field: data.due_date_field ?? '',
      unknown_min_field: data.unknown_min_field ?? '',
      unknown_max_field: data.unknown_max_field ?? '',
      dependency_link_type: data.dependency_link_type ?? '',
      departure_column: data.departure_column ?? '',
    }),
  };
}

function fieldOption(f: TrackerField): SearchableSelectOption {
  return { value: f.id ?? f.name, label: f.id ? `${f.name} (${f.id})` : f.name };
}

/** Guarantee `value` is offered, so a stale or hand-typed id still renders instead of an empty trigger. */
function withOption(options: SearchableSelectOption[], value: string, label = value): SearchableSelectOption[] {
  return value && !options.some((o) => o.value === value) ? [{ value, label }, ...options] : options;
}

/**
 * The two unknown-work pickers share one hint because they share one meaning:
 * they are the ends of a single range, and the forecast reads neither on its
 * own. Two hand-written explanations would drift into describing two
 * different settings.
 */
const UNKNOWN_WORK_HINT =
  'Numeric fields on each deliverable estimating how many work items are still to be created for it. ' +
  'The forecast draws unknown work from the range between the two; ' +
  'left to Shipcast, the values you enter here are kept across imports.';

/**
 * What every cleared tracker field means, in one phrase.
 *
 * It is the same fact each time and it is not "nothing": `apply.rs` writes
 * `CASE WHEN provided THEN … ELSE <existing>`, so an unmapped field is the
 * one Shipcast owns and edits made here survive every re-import. Naming that
 * state three different ways ("Not linked", "Shipcast owns it", "Not
 * estimated") made one behaviour look like three.
 */
const OWNED_BY_SHIPCAST = 'Set in Shipcast';

/** What {@link OWNED_BY_SHIPCAST} means, for the people who have to choose it (shipcast-45y.18). */
const OWNED_BY_SHIPCAST_MEANS =
  'Imports nothing for this field: the value is set in Shipcast, and every re-import keeps it.';

/** The tracker field a setting reads from — a picker, or a free-text id when discovery is down. */
type AdapterFieldName =
  'parent_field' | 'feature_link_field' | 'due_date_field' | 'unknown_min_field' | 'unknown_max_field';

interface AdapterFieldControlProps {
  id: string;
  name: AdapterFieldName;
  describedBy: string | undefined;
  options: SearchableSelectOption[];
  control: Control<FormData>;
  register: UseFormRegister<FormData>;
  discovery: { isLoading: boolean; isError: boolean };
  disabled: boolean;
  error?: string;
  allowClear?: boolean;
}

/**
 * The control half of an adapter-config field.
 *
 * Separate from its row because the unknown-work pair shares one row: a
 * setting and a control are not one-to-one, and folding them together forced
 * the min/max pair to be two rows saying nearly the same sentence.
 *
 * Degrades to a free-text id when discovery fails (tracker unreachable,
 * credentials rejected…) so the dataset stays editable — the same fallback
 * the feature-import due-date picker uses. Only the control swaps; the row
 * around it does not reflow around a failure.
 */
function AdapterFieldControl({
  id,
  name,
  describedBy,
  options,
  control,
  register,
  discovery,
  disabled,
  error,
  allowClear,
}: AdapterFieldControlProps) {
  if (discovery.isError) {
    return <Input {...register(name)} id={id} aria-describedby={describedBy} disabled={disabled} error={error} />;
  }
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <SearchableSelect
          id={id}
          describedBy={describedBy}
          value={field.value || null}
          onChange={(v) => field.onChange(v ?? '')}
          options={withOption(options, field.value)}
          placeholder={discovery.isLoading ? 'Loading fields…' : allowClear ? OWNED_BY_SHIPCAST : 'Pick a field'}
          searchPlaceholder="Search fields..."
          noOptionsMessage={discovery.isLoading ? 'Loading fields…' : 'No fields discovered'}
          allowClear={allowClear}
          clearLabel={OWNED_BY_SHIPCAST}
          clearDescription={OWNED_BY_SHIPCAST_MEANS}
          disabled={disabled || discovery.isLoading}
          error={error}
        />
      )}
    />
  );
}

/**
 * One adapter-config field, as a settings-table row: the setting's name on
 * the left, the picker on the right, the explanation on hover.
 */
function AdapterFieldRow({
  label,
  hint,
  ...control
}: Omit<AdapterFieldControlProps, 'id' | 'describedBy'> & { label: string; hint: string }) {
  const id = useId();
  const failed = control.discovery.isError;

  return (
    <SettingRow
      htmlFor={id}
      label={label}
      hint={failed ? `Field discovery failed — enter the tracker field id manually. ${hint}` : hint}
    >
      {(hintId) => <AdapterFieldControl {...control} id={id} describedBy={hintId} />}
    </SettingRow>
  );
}

/**
 * The unknown-work pair, as one row.
 *
 * They are the two ends of a single estimate, so they are one setting with
 * two inputs rather than two settings — which is also why the row is named by
 * a `role="group"` instead of a `<label for>` pointing at whichever of the
 * two came first.
 */
function UnknownWorkRow({
  control,
  register,
  discovery,
  disabled,
  options,
  allFieldsShown,
  minError,
  maxError,
}: {
  control: Control<FormData>;
  register: UseFormRegister<FormData>;
  discovery: { isLoading: boolean; isError: boolean };
  disabled: boolean;
  options: SearchableSelectOption[];
  /** The numeric filter found nothing to keep, so every field is offered. */
  allFieldsShown: boolean;
  minError?: string;
  maxError?: string;
}) {
  const minId = useId();
  const maxId = useId();
  const failed = discovery.isError;

  return (
    <SettingRow
      label="Unknown work"
      hint={
        failed
          ? `Field discovery failed — enter the tracker field ids manually. ${UNKNOWN_WORK_HINT}`
          : UNKNOWN_WORK_HINT +
            (allFieldsShown ? ' This source types none of its fields as numbers, so all fields are shown.' : '')
      }
    >
      {(hintId) => (
        <div
          className="
            flex flex-col gap-2
            sm:flex-row
          "
        >
          <SettingSubField htmlFor={minId} caption="min">
            <AdapterFieldControl
              id={minId}
              name="unknown_min_field"
              describedBy={hintId}
              options={options}
              control={control}
              register={register}
              discovery={discovery}
              disabled={disabled}
              error={minError}
              allowClear
            />
          </SettingSubField>
          <SettingSubField htmlFor={maxId} caption="max">
            <AdapterFieldControl
              id={maxId}
              name="unknown_max_field"
              describedBy={hintId}
              options={options}
              control={control}
              register={register}
              discovery={discovery}
              disabled={disabled}
              error={maxError}
              allowClear
            />
          </SettingSubField>
        </div>
      )}
    </SettingRow>
  );
}

/**
 * "Start from a board" — the control that removes hand-typed workflow JSON
 * for the common case.
 *
 * The board's own column-to-status mapping is the one part of a dataset a
 * user cannot reconstruct from memory: it needs the tracker's *exact* status
 * names, and getting one wrong is invisible until an ingest reports
 * `unknown_statuses`. So the scaffold is a prefill, not a commitment —
 * everything it writes stays editable, and a source with no boards (or a
 * failed probe) degrades to filling the form by hand rather than blocking.
 *
 * Callers must mount this with `key={sourceId}`: a board id only means
 * anything against the source it came from, so switching sources has to drop
 * the selection. A `key` makes React discard the state, which is both simpler
 * and safer than clearing it in an effect — an effect leaves one render in
 * which a stale id is still selected and "Use board" would scaffold from the
 * wrong tracker.
 */
function BoardScaffoldPicker({
  sourceId,
  disabled,
  onScaffold,
}: {
  sourceId: string;
  disabled: boolean;
  onScaffold: (scaffold: BoardScaffoldResponse) => void;
}) {
  const id = useId();
  const boardsQuery = useSourceBoards(sourceId || null);
  const scaffoldMut = useBoardScaffold();
  const [boardId, setBoardId] = useState<string | null>(null);
  const [applied, setApplied] = useState<{ name: string; hadQuery: boolean } | null>(null);

  const boards = boardsQuery.data ?? [];
  const options: SearchableSelectOption[] = boards.map((b) => ({ value: b.id, label: b.name }));

  const useBoard = async () => {
    if (!boardId) return;
    try {
      const scaffold = await scaffoldMut.mutateAsync({ sourceId, boardId });
      onScaffold(scaffold);
      setApplied({ name: scaffold.name, hadQuery: !!scaffold.query });
    } catch {
      // mutation state carries the error
    }
  };

  if (boardsQuery.isError) {
    return (
      <p className="text-xs text-text-muted">Could not list boards on this source — fill the form in by hand below.</p>
    );
  }

  return (
    <div className="rounded-sm border border-border-default bg-bg-subtle p-3">
      <label htmlFor={id} className="mb-1 block text-sm font-medium">
        Start from a board
      </label>
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <SearchableSelect
            id={id}
            value={boardId}
            onChange={setBoardId}
            options={options}
            placeholder={boardsQuery.isLoading ? 'Loading boards…' : 'Pick a board'}
            searchPlaceholder="Search boards..."
            noOptionsMessage={boardsQuery.isLoading ? 'Loading boards…' : 'No boards on this source'}
            disabled={disabled || boardsQuery.isLoading || boards.length === 0}
          />
        </div>
        <Button
          variant="secondary"
          type="button"
          onClick={useBoard}
          disabled={disabled || !boardId || scaffoldMut.isPending}
        >
          {scaffoldMut.isPending ? (
            <>
              <Loader2 className="size-4 animate-spin" /> Loading…
            </>
          ) : (
            'Use board'
          )}
        </Button>
      </div>
      <p className="mt-1 text-xs text-text-muted">
        Fills in the name here, and on the next steps the deliverables query and the workflow columns with this
        board&apos;s real status names. Everything stays editable afterwards.
      </p>
      {boards.length === 0 && !boardsQuery.isLoading && (
        <p className="mt-1 text-xs text-text-muted">This source reports no boards — fill the form in by hand below.</p>
      )}
      {scaffoldMut.error instanceof Error && (
        <p className="mt-1 text-xs text-feedback-error">{scaffoldMut.error.message}</p>
      )}
      {applied && (
        <p className="mt-1 text-xs text-feedback-success">
          Filled in from “{applied.name}”.
          {!applied.hadQuery &&
            ' This board has no saved filter, so the deliverables query is still empty — write one on the next step.'}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Steps
// ---------------------------------------------------------------------------

/**
 * The form as a sequence of questions, one step each.
 *
 * As one long dialog, a first-time user met every setting at once — two
 * query boxes, a workflow, seven field pickers — with nothing saying which
 * to answer first or which depend on which. The steps are the order the
 * answers depend on each other: the source decides which boards, fields and
 * link types exist; the queries decide which items the workflow and the
 * fields are read from; the review runs the ingest over all of it.
 *
 * Each step's heading is the question it answers, in the domain's words.
 * The rows inside keep their terse noun labels with the sentence on hover
 * (`SettingRow`) — the question belongs to the step, not repeated on every
 * row under it.
 */
const STEPS = [
  {
    id: 'source',
    label: 'Source',
    question: 'Where does the data come from?',
    description: 'The tracker to read from, and a board to start from if it has one.',
  },
  {
    id: 'scope',
    label: 'Scope',
    question: 'Which items are being forecast?',
    description:
      'The deliverables — the items whose delivery dates are forecast — and the work items beneath them that count as their unfinished work and drive throughput.',
  },
  {
    id: 'workflow',
    label: 'Workflow',
    question: 'How does work reach done?',
    description:
      'The tracker’s statuses grouped into columns, in the order work moves through them, and the column that counts as delivered.',
  },
  {
    id: 'fields',
    label: 'Fields',
    question: 'What does Shipcast read from each item?',
    description: 'The tracker fields and links that connect features, deliverables and work items to each other.',
  },
  {
    id: 'review',
    label: 'Review',
    question: 'Does the tracker agree?',
    description: 'Check what a sync of this configuration would record, then save.',
  },
] as const;

type StepId = (typeof STEPS)[number]['id'];

/**
 * Which fields each step owns — what Next validates before leaving it, and
 * where Save sends the user when a field it is refusing is out of sight.
 * A field listed under no step could fail validation invisibly, so every
 * key of the schema appears here exactly once.
 */
const STEP_FIELDS: Record<StepId, readonly (keyof FormData)[]> = {
  source: ['tracker_source_id', 'name'],
  scope: ['deliverables_query', 'work_items_query'],
  workflow: ['workflow_json', 'departure_column'],
  fields: [
    'feature_link_field',
    'due_date_field',
    'unknown_min_field',
    'unknown_max_field',
    'dependency_link_type',
    'parent_field',
    'adapter_config_json',
  ],
  review: [],
};

function stepIndex(id: StepId): number {
  return STEPS.findIndex((s) => s.id === id);
}

/** A definition-list row of the review summary. */
function SummaryRow({ term, children }: { term: string; children: ReactNode }) {
  return (
    <div
      className="
        grid gap-x-6 gap-y-0.5 py-1.5
        sm:grid-cols-[minmax(0,14rem)_minmax(0,1fr)]
      "
    >
      <dt className="text-sm text-text-muted">{term}</dt>
      <dd className="min-w-0 text-sm text-text-primary">{children}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Form dialog
// ---------------------------------------------------------------------------

export function DatasetFormDialog({
  dataset,
  sources,
  onClose,
}: {
  dataset: DatasetResponse | null;
  sources: SourceResponse[];
  onClose: () => void;
}) {
  const isEditing = !!dataset;
  const createMut = useCreateDataset();
  const updateMut = useUpdateDataset();
  const defaultSplit = splitAdapterConfig(DEFAULT_ADAPTER_CONFIG);
  const adapterJsonId = useId();
  // Every control is label-linked: `htmlFor` is what lets a screen reader
  // announce the field, and what lets a test find it by its visible name.
  const sourceSelectId = useId();
  const deliverablesQueryId = useId();
  const workItemsQueryId = useId();
  const departureColumnId = useId();
  const dependencyLinkTypeId = useId();
  const nameId = useId();
  const stepHeadingId = useId();

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    getFieldState,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      tracker_source_id: sources[0]?.id ?? '',
      deliverables_query: '',
      work_items_query: '',
      workflow_json: JSON.stringify(DEFAULT_WORKFLOW, null, 2),
      ...defaultSplit.picked,
      // Overrides the split's server-mirror `done`: a new dataset's delivered
      // column must BE one of its workflow's columns. See the constant.
      departure_column: DEFAULT_DEPARTURE_COLUMN,
      adapter_config_json: defaultSplit.restJson,
    },
  });

  useEffect(() => {
    if (dataset) {
      const split = splitAdapterConfig(dataset.adapter_config);
      reset({
        name: dataset.name,
        tracker_source_id: dataset.tracker_source_id,
        deliverables_query: dataset.deliverables_query,
        work_items_query: dataset.work_items_query,
        workflow_json: JSON.stringify(dataset.workflow, null, 2),
        ...split.picked,
        adapter_config_json: split.restJson,
      });
    }
  }, [dataset, reset]);

  // -- Step navigation -----------------------------------------------------

  const [step, setStep] = useState<StepId>('source');
  const current = STEPS.find((s) => s.id === step) ?? STEPS[0];
  const previous = STEPS[stepIndex(step) - 1]?.id ?? null;
  const next = STEPS[stepIndex(step) + 1]?.id ?? null;

  /**
   * Move to a step. Backwards is always free — a user going back to fix
   * something must not be held by the very error they are going to fix.
   * Forwards validates each step being skipped over, in order, and stops on
   * the first one that fails: the errors are then on screen, on the step
   * that owns them, rather than reported later on a step that cannot show
   * them.
   */
  const goTo = async (target: StepId) => {
    const from = stepIndex(step);
    const to = stepIndex(target);
    for (const passed of STEPS.slice(from, Math.max(from, to))) {
      if (!(await trigger(STEP_FIELDS[passed.id]))) {
        setStep(passed.id);
        return;
      }
    }
    setStep(target);
  };

  // Focus the new step's heading, so keyboard and screen-reader users land on
  // the question rather than wherever focus was left — typically the Next
  // button that has just moved. Not on first render: the name field's
  // `autoFocus` owns that.
  const headingRef = useRef<HTMLHeadingElement>(null);
  const previousStep = useRef(step);
  useEffect(() => {
    if (previousStep.current !== step) {
      previousStep.current = step;
      headingRef.current?.focus();
    }
  }, [step]);

  /**
   * Copy a board scaffold into the form.
   *
   * `shouldValidate` so a prefilled field clears its own "required" error
   * instead of staying red until touched. The query is written only when the
   * board actually has one — a board with no saved filter leaves the field
   * empty on purpose, and the picker says so, because inventing a query here
   * would silently ingest the wrong items.
   */
  const applyScaffold = (scaffold: BoardScaffoldResponse) => {
    setValue('name', scaffold.name, { shouldValidate: true });
    setValue('workflow_json', JSON.stringify({ columns: scaffold.columns }, null, 2), { shouldValidate: true });
    // A delivered column nobody chose is the template's last column, which the
    // board's columns just replaced: follow them (shipcast-45y.6). One the user
    // picked, or an existing dataset stored, is never repointed.
    if (!isEditing && !getFieldState('departure_column').isDirty) {
      setValue('departure_column', scaffold.columns[scaffold.columns.length - 1]?.name ?? '', { shouldValidate: true });
    }
    if (scaffold.query) {
      setValue('deliverables_query', scaffold.query, { shouldValidate: true });
    }
  };

  const onSave = async (data: FormData) => {
    try {
      const payload = { name: data.name, ...datasetBody(data) };
      if (isEditing && dataset) {
        await updateMut.mutateAsync({ id: dataset.id, ...payload });
      } else {
        await createMut.mutateAsync(payload);
      }
      onClose();
    } catch {
      // mutation state carries the error
    }
  };

  /**
   * Save was pressed with a field still invalid. The only way there is a
   * direct jump past the step that owns it, so show that step: an error on
   * a field that is not on screen is an error the user cannot act on.
   */
  const onInvalid = (invalid: Partial<Record<keyof FormData, unknown>>) => {
    const owner = STEPS.find((s) => STEP_FIELDS[s.id].some((field) => field in invalid));
    if (owner) setStep(owner.id);
  };

  // Enter in a text field submits the form. Before the last step that means
  // "next", never "save": a dataset must not be created from the Scope step
  // by an Enter meant for a query box.
  const onSubmit =
    next === null
      ? handleSubmit(onSave, onInvalid)
      : (event: FormEvent) => {
          event.preventDefault();
          void goTo(next);
        };

  const busy = isSubmitting || createMut.isPending || updateMut.isPending;
  const mutationError = createMut.error || updateMut.error;

  // -- Derived options -----------------------------------------------------

  // `useWatch` rather than `watch`: the latter makes the React Compiler skip this component.
  const sourceId = useWatch({ control, name: 'tracker_source_id' });
  const workflowJson = useWatch({ control, name: 'workflow_json' });
  const departureColumn = useWatch({ control, name: 'departure_column' });
  // Watched so an edited query drops its stale preview result. This re-renders
  // per keystroke but never fetches — the preview only runs on its button.
  const deliverablesQuery = useWatch({ control, name: 'deliverables_query' });
  const workItemsQuery = useWatch({ control, name: 'work_items_query' });
  const name = useWatch({ control, name: 'name' });

  // The whole form, for the pre-save preview: its stale-result check needs
  // to see every field Save would send, not a hand-picked subset that could
  // miss the one that changed. `null` while the form cannot yet produce a
  // body (unparseable JSON) — the field's own error is already showing.
  const formValues = useWatch({ control });
  const previewBody = useMemo(() => {
    try {
      return datasetBody(formValues);
    } catch {
      return null;
    }
  }, [formValues]);

  // The JSON escape hatch earns its place only when it holds something.
  // Every key this build knows now has a control of its own, so for a config
  // this build wrote it is `{}` — an empty box labelled "other adapter
  // config" invites someone to invent a key. It reappears for a row written
  // by a LATER shipcast carrying a key with no control here, which the
  // tolerant reader keeps and this is the only place to see or fix.
  const adapterConfigJson = useWatch({ control, name: 'adapter_config_json' });
  const hasUnmappedKeys = useMemo(() => {
    try {
      return Object.keys(JSON.parse(adapterConfigJson || '{}')).length > 0;
    } catch {
      // Unparseable — stay visible, or the user cannot reach what blocks the save.
      return true;
    }
  }, [adapterConfigJson]);

  // Status vocabulary for the workflow editor: the raw names the last ingest
  // saw that no column claimed. `observed` is absent on ingestions recorded
  // before it existed, which reads as "no suggestions" — the honest answer,
  // since that run genuinely did not record them.
  const latestIngestion = useLatestIngestion(dataset?.id);
  const observedStatuses = useMemo(
    () =>
      (latestIngestion.data?.latest?.result?.batch.warnings ?? []).flatMap((w) =>
        w.kind === 'unknown_statuses' ? (w.observed ?? []) : [],
      ),
    [latestIngestion.data],
  );

  // The departure column must name a column that exists, so the options come
  // from the editor rather than free text. The stored value is offered even
  // when it matches nothing — dropping it would silently repoint a
  // misconfigured dataset at another column instead of showing the mismatch.
  const workflowColumns = useMemo(
    () =>
      parseWorkflowColumns(workflowJson)
        ?.map((c) => c.name)
        .filter((n) => n.length > 0) ?? [],
    [workflowJson],
  );
  const departureOptions = useMemo(
    () =>
      departureColumn && !workflowColumns.includes(departureColumn)
        ? [departureColumn, ...workflowColumns]
        : workflowColumns,
    [workflowColumns, departureColumn],
  );
  const fieldsQuery = useSourceFields(sourceId || null);
  const fields = fieldsQuery.data;
  const [showAllFields, setShowAllFields] = useState(false);
  const parentLike = useMemo(() => (fields ?? []).filter((f) => f.field_type === 'work_item_ref'), [fields]);
  // A source may report no field types at all — then the parent-shaped filter
  // would hide every field, so show everything rather than an empty picker.
  const noParentLike = !!fields && fields.length > 0 && parentLike.length === 0;
  const showAll = showAllFields || noParentLike;
  const fieldOptions = useMemo(
    () => (showAll ? (fields ?? []) : parentLike).map(fieldOption),
    [showAll, fields, parentLike],
  );
  // Jira Cloud discovers `parent` as a field; other trackers may not, yet the
  // domain selector `parent` is always valid — so it is always on offer.
  const parentOptions = withOption(fieldOptions, 'parent', 'Parent (built-in)');

  // The tracker's own link-type vocabulary. The name is the load-bearing
  // half — upstream pins it equal to what lands in `Link::link_type`, which
  // is what a config is matched against — so the wording is decoration on
  // the label and never the stored value. Without it "Blocks" alone does not
  // say which end of the relationship is being configured.
  const linkTypesQuery = useSourceLinkTypes(sourceId || null);
  const linkTypeOptions = useMemo(
    () =>
      (linkTypesQuery.data ?? []).map((t) => ({
        value: t.name,
        label: t.inward ? `${t.name} (${t.inward})` : t.name,
      })),
    [linkTypesQuery.data],
  );

  const sourceOptions = useMemo(() => sources.map((s) => ({ value: s.id, label: `${s.name} (${s.kind})` })), [sources]);
  const source = sources.find((s) => s.id === sourceId);

  // The remaining pickers need their own lists: `fieldOptions` is narrowed to
  // work-item pointers (and the checkbox that widens it is labelled for that
  // question alone), which is the wrong shape for a date or a number.
  const allFieldOptions = useMemo(() => (fields ?? []).map(fieldOption), [fields]);
  const dateLike = useMemo(
    () => (fields ?? []).filter((f) => f.field_type === 'date' || f.field_type === 'datetime'),
    [fields],
  );
  // Same guard as `noParentLike`: a source that types none of its fields as
  // dates would otherwise get a picker offering only the built-in.
  const noDateLike = !!fields && fields.length > 0 && dateLike.length === 0;
  const dueDateOptions = withOption(
    noDateLike ? allFieldOptions : dateLike.map(fieldOption),
    'due_date',
    'Due date (built-in)',
  );
  // Same guard again: a source that types none of its fields as numbers
  // would otherwise get two empty pickers for the unknown-work range.
  const numeric = useMemo(() => (fields ?? []).filter((f) => f.field_type === 'number'), [fields]);
  const noNumeric = !!fields && fields.length > 0 && numeric.length === 0;
  const numericOptions = noNumeric ? allFieldOptions : numeric.map(fieldOption);

  const queryBoxClass = `
    w-full rounded-sm border px-3 py-2 font-mono text-sm
    disabled:bg-bg-muted
  `;

  return (
    <Dialog
      title={isEditing ? 'Edit dataset' : 'New dataset'}
      onClose={onClose}
      dismissible={!busy}
      // Wide enough for the settings table below to put a label and its
      // control side by side; at max-w-lg they stack and the table reads as
      // the same single column it replaced.
      maxWidth="max-w-4xl"
    >
      <form onSubmit={onSubmit} className="space-y-6" noValidate>
        <StepNav steps={STEPS} current={step} onSelect={(id) => void goTo(id as StepId)} disabled={busy} />

        {/* One named region per step: it is what a screen reader announces on
            entering, and what lets a test assert that a control lives on the
            step it belongs to. */}
        <section aria-labelledby={stepHeadingId} className="space-y-6">
          <div>
            <h3
              id={stepHeadingId}
              ref={headingRef}
              tabIndex={-1}
              className="
                text-base font-semibold text-text-primary
                focus:outline-none
              "
            >
              {current.question}
            </h3>
            <p className="mt-1 text-sm text-text-muted">{current.description}</p>
          </div>

          {step === 'source' && (
            <>
              {/* Source first, then the name: the board scaffolds a name, so
                  asking for one before anything knows what the dataset is
                  makes the user invent a label and then discard it. */}
              <SettingsTable>
                <SettingRow
                  htmlFor={sourceSelectId}
                  label="Tracker"
                  hint="The configured Jira or Azure DevOps instance this dataset reads from."
                >
                  {(hintId) => (
                    <Controller
                      control={control}
                      name="tracker_source_id"
                      render={({ field }) => (
                        <SearchableSelect
                          id={sourceSelectId}
                          describedBy={hintId}
                          value={field.value || null}
                          onChange={(v) => field.onChange(v ?? '')}
                          options={sourceOptions}
                          placeholder="Pick a tracker"
                          searchPlaceholder="Search trackers..."
                          disabled={busy}
                          error={errors.tracker_source_id?.message}
                        />
                      )}
                    />
                  )}
                </SettingRow>
                <SettingRow
                  htmlFor={nameId}
                  label="Dataset name"
                  hint="Shown wherever this dataset is picked. Starting from a board fills this in for you."
                >
                  {(hintId) => (
                    <Input
                      {...register('name')}
                      id={nameId}
                      aria-describedby={hintId}
                      placeholder="Backend team epics"
                      error={errors.name?.message}
                      disabled={busy}
                      autoFocus={!isEditing}
                    />
                  )}
                </SettingRow>
              </SettingsTable>
              {sourceId && (
                <BoardScaffoldPicker key={sourceId} sourceId={sourceId} disabled={busy} onScaffold={applyScaffold} />
              )}
            </>
          )}

          {step === 'scope' && (
            <SettingsTable>
              <SettingRow
                htmlFor={deliverablesQueryId}
                label="Deliverables query"
                hint="Tracker query (JQL for Jira, WIQL for Azure DevOps) selecting the items to forecast — typically epics."
              >
                {(hintId) => (
                  <>
                    <textarea
                      {...register('deliverables_query')}
                      id={deliverablesQueryId}
                      aria-describedby={hintId}
                      rows={2}
                      className={queryBoxClass}
                      placeholder="project = ACME AND type = Epic"
                      disabled={busy}
                    />
                    {errors.deliverables_query && (
                      <p className="mt-1 text-xs text-feedback-error">{errors.deliverables_query.message}</p>
                    )}
                    <QueryPreviewPanel
                      sourceId={sourceId || null}
                      query={deliverablesQuery ?? ''}
                      label="deliverables query"
                      disabled={busy}
                    />
                  </>
                )}
              </SettingRow>
              <SettingRow
                htmlFor={workItemsQueryId}
                label="Work items query"
                hint="Tracker query selecting the broader set beneath the deliverables — typically stories, bugs and tasks."
              >
                {(hintId) => (
                  <>
                    <textarea
                      {...register('work_items_query')}
                      id={workItemsQueryId}
                      aria-describedby={hintId}
                      rows={2}
                      className={queryBoxClass}
                      placeholder="project = ACME AND type in (Story, Bug)"
                      disabled={busy}
                    />
                    {errors.work_items_query && (
                      <p className="mt-1 text-xs text-feedback-error">{errors.work_items_query.message}</p>
                    )}
                    <QueryPreviewPanel
                      sourceId={sourceId || null}
                      query={workItemsQuery ?? ''}
                      label="work-items query"
                      disabled={busy}
                    />
                  </>
                )}
              </SettingRow>
            </SettingsTable>
          )}

          {step === 'workflow' && (
            <>
              <Controller
                control={control}
                name="workflow_json"
                render={({ field }) => (
                  <WorkflowEditor
                    value={field.value}
                    onChange={field.onChange}
                    observedStatuses={observedStatuses}
                    disabled={busy}
                    error={errors.workflow_json?.message}
                  />
                )}
              />
              {/* The column that counts as shipped belongs with the workflow:
                  it must BE one of the workflow's columns, and a name
                  matching none of them means nothing is ever counted as done. */}
              <SettingsTable>
                <SettingRow
                  htmlFor={departureColumnId}
                  label="Delivered column"
                  hint="Work items are counted as delivered when they reach this column. It must be one of the workflow columns above — a name that matches none of them means nothing is ever counted as done."
                >
                  {(hintId) => (
                    <Controller
                      control={control}
                      name="departure_column"
                      render={({ field }) => (
                        <SearchableSelect
                          id={departureColumnId}
                          describedBy={hintId}
                          value={field.value || null}
                          onChange={(v) => field.onChange(v ?? '')}
                          options={withOption(
                            departureOptions.map((n) => ({ value: n, label: n })),
                            field.value,
                          )}
                          placeholder="Pick a column"
                          searchPlaceholder="Search columns..."
                          disabled={busy}
                          error={errors.departure_column?.message}
                        />
                      )}
                    />
                  )}
                </SettingRow>
              </SettingsTable>
            </>
          )}

          {step === 'fields' && (
            <>
              {/* Visible, not on hover: the two link fields are the one pair
                  a first-time user reliably confuses, because both are
                  "the parent field" on the tracker and they point in opposite
                  directions. docs/tracker-integration.md, "Distinct from the
                  existing parent_field knob". */}
              <p className="rounded-sm border border-border-default bg-bg-subtle p-3 text-xs text-text-secondary">
                Two links, opposite directions. <strong>Feature link</strong> is read off each deliverable and points up
                to its feature — an epic’s initiative. <strong>Deliverable link</strong> is read off each work item and
                points up to its deliverable — a story’s epic. On older Jira instances they are different custom fields;
                setting one never implies the other.
              </p>
              {/* Each level owns the fields read off its own items, so
                  "which field links a deliverable to its feature?" is
                  answerable by looking. */}
              <SettingsSection title="Deliverables" description="Read off each item the deliverables query selects.">
                <AdapterFieldRow
                  name="feature_link_field"
                  label="Feature link"
                  hint="Field on each deliverable naming the feature it belongs to — an epic's initiative, say. Left to Shipcast, the grouping you set here is kept across imports. A mapped link groups the team's deliverables at its next Deliverables refresh; a feature missing from the list shows as a warning on that refresh."
                  options={fieldOptions}
                  control={control}
                  register={register}
                  discovery={fieldsQuery}
                  disabled={busy}
                  error={errors.feature_link_field?.message}
                  allowClear
                />
                {/* Every one is optional in the sense that matters: leave it
                    unmapped and Shipcast owns that value instead of the
                    tracker. */}
                <AdapterFieldRow
                  name="due_date_field"
                  label="Due date"
                  hint={
                    'Date field on each deliverable holding its due date. ' +
                    'Left to Shipcast, the dates you enter here are kept across imports.' +
                    (noDateLike ? ' This source types none of its fields as dates, so all fields are shown.' : '')
                  }
                  options={dueDateOptions}
                  control={control}
                  register={register}
                  discovery={fieldsQuery}
                  disabled={busy}
                  error={errors.due_date_field?.message}
                  allowClear
                />
                <UnknownWorkRow
                  options={numericOptions}
                  allFieldsShown={noNumeric}
                  control={control}
                  register={register}
                  discovery={fieldsQuery}
                  disabled={busy}
                  minError={errors.unknown_min_field?.message}
                  maxError={errors.unknown_max_field?.message}
                />
                <SettingRow
                  htmlFor={dependencyLinkTypeId}
                  label="Dependency link type"
                  hint={
                    'The tracker’s own name for the link that makes one deliverable wait for another — Blocks in Jira, Dependency in Azure DevOps. ' +
                    'Only inbound blocking links of this exact name become dependencies, so a name the tracker does not use imports none at all.' +
                    (linkTypeOptions.length === 0
                      ? ' This tracker reported no link types, so the name has to be typed.'
                      : '')
                  }
                >
                  {(hintId) =>
                    // Empty and failed are the same answer — "we do not know this
                    // tracker's vocabulary" — so both degrade to free text rather
                    // than to a picker with nothing in it. Both trackers report
                    // their types now (Azure DevOps derives its names, and lists
                    // them through the same derivation); a source on an older
                    // pin, or one whose probe failed, still lands here.
                    linkTypeOptions.length === 0 ? (
                      <Input
                        {...register('dependency_link_type')}
                        id={dependencyLinkTypeId}
                        aria-describedby={hintId}
                        placeholder="Blocks"
                        error={errors.dependency_link_type?.message}
                        disabled={busy}
                      />
                    ) : (
                      <Controller
                        control={control}
                        name="dependency_link_type"
                        render={({ field }) => (
                          <SearchableSelect
                            id={dependencyLinkTypeId}
                            describedBy={hintId}
                            value={field.value || null}
                            onChange={(v) => field.onChange(v ?? '')}
                            options={withOption(linkTypeOptions, field.value)}
                            placeholder="Pick a link type"
                            searchPlaceholder="Search link types..."
                            disabled={busy || linkTypesQuery.isLoading}
                            error={errors.dependency_link_type?.message}
                          />
                        )}
                      />
                    )
                  }
                </SettingRow>
              </SettingsSection>
              <SettingsSection title="Work items" description="Read off each item the work items query selects.">
                <AdapterFieldRow
                  name="parent_field"
                  label="Deliverable link"
                  hint="Field on each work item naming the deliverable it counts towards."
                  options={parentOptions}
                  control={control}
                  register={register}
                  discovery={fieldsQuery}
                  disabled={busy}
                  error={errors.parent_field?.message}
                />
              </SettingsSection>
              {!fieldsQuery.isError && !noParentLike && (fields?.length ?? 0) > 0 && (
                <Checkbox
                  label="Show all fields, not only the ones that point at other work items"
                  checked={showAllFields}
                  onChange={(e) => setShowAllFields(e.target.checked)}
                  disabled={busy}
                />
              )}
              {noParentLike && (
                <p className="text-xs text-text-muted">
                  This source reports no fields that point at other work items — showing all fields.
                </p>
              )}
              {hasUnmappedKeys && (
                <SettingsSection
                  title="Advanced"
                  description="Adapter settings this version has no control for — most likely written by a newer Shipcast. They are kept as-is."
                >
                  <SettingRow
                    htmlFor={adapterJsonId}
                    label="Unrecognised keys (JSON)"
                    hint="Everything except the settings above, which replace any copy pasted here."
                  >
                    {(hintId) => (
                      <>
                        <textarea
                          {...register('adapter_config_json')}
                          id={adapterJsonId}
                          aria-describedby={hintId}
                          rows={4}
                          className="
                            w-full rounded-sm border px-3 py-2 font-mono text-xs
                            disabled:bg-bg-muted
                          "
                          disabled={busy}
                        />
                        {errors.adapter_config_json && (
                          <p className="mt-1 text-xs text-feedback-error">{errors.adapter_config_json.message}</p>
                        )}
                      </>
                    )}
                  </SettingRow>
                </SettingsSection>
              )}
            </>
          )}

          {step === 'review' && (
            <>
              {/* The answers so far, in one screen, so the preview below is
                  read against what it previewed — and a wrong answer is one
                  click away, on the step that owns it. */}
              <dl className="divide-y divide-border-default rounded-sm border border-border-default px-4">
                <SummaryRow term="Tracker">{source ? `${source.name} (${source.kind})` : '—'}</SummaryRow>
                <SummaryRow term="Dataset name">{name || '—'}</SummaryRow>
                <SummaryRow term="Deliverables query">
                  <code className="text-xs">{deliverablesQuery || '—'}</code>
                </SummaryRow>
                <SummaryRow term="Work items query">
                  <code className="text-xs">{workItemsQuery || '—'}</code>
                </SummaryRow>
                <SummaryRow term="Workflow">
                  {workflowColumns.length === 0
                    ? '—'
                    : `${workflowColumns.join(' → ')}; delivered at ${departureColumn || '—'}`}
                </SummaryRow>
              </dl>
              <DatasetPreviewPanel body={previewBody} disabled={busy} />
            </>
          )}
        </section>

        {mutationError instanceof Error && <p className="text-sm text-feedback-error">{mutationError.message}</p>}

        <DialogActions>
          <Button variant="ghost" type="button" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          {previous !== null && (
            <Button variant="secondary" type="button" onClick={() => void goTo(previous)} disabled={busy}>
              Back
            </Button>
          )}
          {next === null ? (
            <Button variant="primary" type="submit" disabled={busy}>
              {busy ? (
                <>
                  <Loader2 className="size-4 animate-spin" /> Saving…
                </>
              ) : isEditing ? (
                'Save'
              ) : (
                'Create'
              )}
            </Button>
          ) : (
            <Button variant="primary" type="submit" disabled={busy}>
              Next
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
}
